import { Anchor, Box, Stack } from '@mantine/core';
import type { GrantType } from '@repo/api-codegen';
import {
	SettingsMultiSelect,
	SettingsSelect,
	SettingsSwitch,
} from '@repo/common/components';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { useLocalStorageBooleanState } from '@repo/common/hooks/useLocalStorageBooleanState';
import type { IIntegration } from '@repo/common/models/integration';
import { Banner, Text, Title } from '@repo/foundations';
import { upperFirst } from 'lodash-es';
import { Link } from 'react-router';
import { useUpdateIntegration, useWorkspace } from '../../../../../api';
import { getPermissionRequirementsForIntegrationType } from './permissionRequirements';

const PRIVILEGES = ['SELECT', 'INSERT', 'UPDATE', 'DELETE'] as const;

const LABEL_MAP: Partial<Record<GrantType, string>> = {
	manual: 'Manual',
	create_temporary_user_and_role: 'Create temporary user',
	grant_privileges_to_role: 'Select existing role',
	create_and_grant_temporary_role: 'Select existing user (intermediary role)',
	grant_privileges_to_user_direct: 'Select existing user (direct access)',
};
interface AccessRequestPreferencesProps {
	integration: IIntegration;
}

// Access request approval types
type AccessRequestType = IIntegration['access_request_preference'];

export function AccessRequestPreferences({
	integration,
}: AccessRequestPreferencesProps) {
	const { mutateAsync: updateIntegration } = useUpdateIntegration({});
	const { workspace } = useWorkspace();
	const [
		accessRequestBannerDismissed,
		{ setTrue: dismissAccessRequestBanner },
	] = useLocalStorageBooleanState('accessRequestBannerShown', false);

	const accessRequestType = integration.access_request_preference || 'manual';

	const allowedPrivileges = integration.access_request_allowed_privileges || [
		'SELECT',
		'INSERT',
		'UPDATE',
		'DELETE',
	];

	const permissionRequirements = getPermissionRequirementsForIntegrationType(
		integration.type
	);

	// Find the integration definition to check if it supports data access requests
	const integrationDefinition = integrationList.find(
		(def) => def.type === integration.type
	);
	const supportsDataAccessRequests =
		integrationDefinition?.supportsDataAccessRequests ?? false;

	return (
		<Stack spacing="md">
			<Title size="md">Access requests</Title>
			{integration.access_request_enabled &&
				permissionRequirements &&
				!accessRequestBannerDismissed && (
					<Banner
						tone="info"
						title="Required Database Permissions"
						message={
							<span
								dangerouslySetInnerHTML={{
									__html: permissionRequirements,
								}}
								aria-label="Database permissions required for data access requests"
							/>
						}
						onDismiss={() => dismissAccessRequestBanner()}
					/>
				)}
			{!workspace?.dar_enabled && (
				<Banner
					tone="info"
					message={
						<Text size="xs">
							To allow access requests, enable the feature in{' '}
							<Anchor component={Link} to="/settings/data-access-requests">
								Requests settings
							</Anchor>
							.
						</Text>
					}
				/>
			)}
			<Box>
				<SettingsSwitch
					title="Allow access requests"
					description={`Allow workspace members to request access to ${upperFirst(integration.type)} resources`}
					disabled={!workspace?.dar_enabled}
					checked={!!integration.access_request_enabled}
					testId="access-request-enabled-toggle"
					onChange={(checked) => {
						let accessRequestPreference = integration.access_request_preference;
						// If the integration does not support the "default" access
						// request preference prepopulated on the django database, set it
						// to the first supported preference
						if (
							!accessRequestPreference ||
							!integrationDefinition?.supportsDataAccessRequestPreferences?.includes(
								accessRequestPreference
							)
						) {
							accessRequestPreference =
								integrationDefinition
									?.supportsDataAccessRequestPreferences?.[0];
						}

						updateIntegration({
							data: {
								id: integration.id,
								access_request_enabled: checked,
								access_request_preference: accessRequestPreference,
							},
						});
					}}
				/>
				{integration.access_request_enabled && (
					<>
						<SettingsSelect
							title="Access request approval type"
							options={
								integrationDefinition?.supportsDataAccessRequestPreferences?.map(
									(preference) => ({
										label: LABEL_MAP[preference as keyof typeof LABEL_MAP],
										value: preference,
									})
								) ?? []
							}
							value={accessRequestType}
							onChange={async (value: string | null) => {
								if (!value) return;
								await updateIntegration({
									data: {
										id: integration.id,
										access_request_preference: value as AccessRequestType,
									},
								});
							}}
						/>

						{supportsDataAccessRequests && accessRequestType !== 'manual' && (
							<SettingsMultiSelect
								title="Allowed privileges"
								options={PRIVILEGES.map((privilege) => ({
									label: privilege,
									value: privilege,
								}))}
								values={allowedPrivileges}
								onChange={async (values: string[]) => {
									await updateIntegration({
										data: {
											id: integration.id,
											access_request_allowed_privileges: values as Array<
												'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE'
											>,
										},
									});
								}}
								itemName="privilege"
							/>
						)}
					</>
				)}
			</Box>

			{integration.access_request_enabled &&
				accessRequestType === 'grant_privileges_to_user_direct' && (
					<Banner
						title="Warning"
						tone="warning"
						message="Using 'Select existing user (direct assignment)' will not create an intermediary role. When access is revoked or expires, pre-existing access to databases and schemas may be removed. It is recommended to use 'Select existing user (intermediary role)' instead."
					/>
				)}
		</Stack>
	);
}
